"use client";

import * as React from "react";
import {
	animate,
	AnimatePresence,
	type Easing,
	m,
	useMotionValue,
	useReducedMotion,
	useTransform,
} from "motion/react";
import { useIsomorphicLayoutEffect } from "usehooks-ts";

import { MotionFeatures } from "../../lib/motion-features";
import { cn } from "../../lib/utils";
import { Text, type TextElement, type TextProps } from "./text";

/**
 * Pinned rather than left to the runtime default, which resolves differently on the
 * Node server and in the browser and would render two different strings for the same
 * `value` — a hydration mismatch. Callers needing another locale pass their own `format`.
 */
const integerFormatter = new Intl.NumberFormat("en-AU");

/**
 * Default formatter: a grouped whole number. Hoisted so the `Intl.NumberFormat`
 * instance is built once rather than per render.
 */
const formatInteger = (value: number) =>
	integerFormatter.format(Math.round(value));

/** A roll is one slide; a count has the whole range to cover, so they want different lengths. */
const defaultDurations = { roll: 0.4, count: 1.2 } as const;

type AnimatedNumberAnimation = keyof typeof defaultDurations;

/**
 * Rolls each changed character vertically, the way a flip clock or odometer does.
 *
 * A single `AnimatePresence` covers all three cases through one mechanism, because each
 * character is keyed by its column *and* its character. A character that did not change
 * keeps its key and stays perfectly still; one that changed swaps keys, so the old rolls
 * out as the new rolls in; and a column the figure gained or lost is just a key arriving
 * or leaving. Nesting a second `AnimatePresence` per column looks equivalent but is not —
 * a column added that way mounts without an entrance and snaps into place.
 *
 * The figure jumps to its target rather than interpolating: at 60fps the ones column
 * would change every frame, and restarting a slide that often reads as a blur rather
 * than a roll.
 *
 * One known limitation. When a change in length leaves some characters in place but moves
 * them sideways, as 999 to 1,999 does to its three nines, they take their new column
 * immediately rather than sliding to it. A `layout` animation is the obvious answer and
 * has no effect here, because `popLayout` has already taken the departing characters out
 * of the flow by the time it would measure. Motion's own `AnimateNumber` does animate this,
 * so it is a real if narrow gap; the characters that changed still roll normally.
 */
/**
 * Read through `custom` rather than closed over, because a character on its way out was
 * rendered before the change that removed it. Inline props would leave it exiting in the
 * direction of the *previous* change, so the first time a figure reverses the outgoing and
 * incoming characters cross over each other instead of travelling together.
 */
const characterVariants = {
	enter: (direction: number) => ({ y: `${direction * 100}%`, opacity: 0 }),
	settled: { y: "0%", opacity: 1 },
	exit: (direction: number) => ({ y: `${direction * -100}%`, opacity: 0 }),
};

function RollingCharacter({
	character,
	direction,
	duration,
	ease,
	ref,
}: {
	character: string;
	direction: number;
	duration: number;
	ease: Easing | Easing[];
	// `popLayout` measures the child it is handed so it can take an exiting one out of the
	// flow without the row jumping. In React 19 `ref` is an ordinary prop.
	ref?: React.Ref<HTMLSpanElement>;
}) {
	return (
		<m.span
			ref={ref}
			data-slot="animated-number-character"
			data-testid="animated-number-character"
			className="inline-block"
			custom={direction}
			variants={characterVariants}
			initial="enter"
			animate="settled"
			exit="exit"
			transition={{ duration, ease }}
		>
			{/* A literal space collapses inside an inline-block. */}
			{character === " " ? "\u00A0" : character}
		</m.span>
	);
}

/**
 * A stable identity for the character at `index`.
 *
 * Digits are counted from the right, so the ones column keeps its key, and therefore
 * stays still, when the figure gains or loses a digit on the left. A leading prefix such
 * as a currency symbol has to be counted from the left instead: its distance from the
 * right changes with every digit added, which would give it a new key and make it
 * unmount and remount rather than sit where it is.
 *
 * The character is part of the key, so a column whose character is unchanged keeps its
 * identity and one whose character changed swaps for a new element to roll in.
 */
function characterKey(
	characters: string[],
	index: number,
	prefixLength: number,
) {
	const column =
		index < prefixLength
			? `prefix-${index}`
			: `${characters.length - 1 - index}`;
	return `${column}-${characters[index]}`;
}

/**
 * Any decimal numeral, not just `0-9`. A caller formatting in, say, `ar-EG` gets
 * Arabic-Indic digits, and `\d` would find none of them and treat the whole figure as
 * prefix, keying every character from the left.
 */
const decimalNumeral = /\p{Decimal_Number}/u;

/** Length of the leading run of non-digits, e.g. `"$"` in `"$1,000"` or `"-$"` in `"-$40"`. */
function leadingPrefixLength(characters: string[]) {
	const firstDigit = characters.findIndex((character) =>
		decimalNumeral.test(character),
	);
	return firstDigit === -1 ? characters.length : firstDigit;
}

/** The row of characters making up the figure. */
function RollingFigure({
	text,
	direction,
	duration,
	ease,
}: {
	text: string;
	direction: number;
	duration: number;
	ease: Easing | Easing[];
}) {
	const characters = Array.from(text);
	const prefixLength = leadingPrefixLength(characters);

	return (
		// Clipping the row rather than each character keeps them all on one baseline, and
		// `relative` anchors the ones `popLayout` takes out of the flow on their way out.
		//
		// Only the vertical axis is clipped. `overflow-hidden` would also clip horizontally,
		// cutting off a character mid-slide when the figure shrinks: `popLayout` narrows the
		// row the moment the departing characters leave the flow, while the ones that stay
		// are still travelling to their new columns. `clip` on one axis leaves the other
		// genuinely visible, which `hidden` would not.
		<span className="relative inline-flex [overflow:visible_clip]">
			{/* `custom` reaches exiting characters too, so a reversal turns them all around. */}
			<AnimatePresence initial={false} mode="popLayout" custom={direction}>
				{characters.map((character, index) => (
					<RollingCharacter
						key={characterKey(characters, index, prefixLength)}
						character={character}
						direction={direction}
						duration={duration}
						ease={ease}
					/>
				))}
			</AnimatePresence>
		</span>
	);
}

/** Drives {@link RollingFigure}, holding the text back a beat so the mount also rolls. */
function Roll({
	value,
	from,
	format,
	duration,
	ease,
	prefersReducedMotion,
}: {
	value: number;
	from: number;
	format: (value: number) => string;
	duration: number;
	ease: Easing | Easing[];
	prefersReducedMotion: boolean;
}) {
	// Starts at `from` so the server and the client's first render agree, then rolls to
	// `value` before paint. Rendering `value` directly would mismatch on hydration.
	const [figure, setFigure] = React.useState(() => ({
		text: format(from),
		direction: 1,
	}));

	// Tracked in a ref that is only ever written from a committed effect. Updating it
	// during render would survive a render React then throws away, leaving a later
	// commit to roll the wrong way.
	const previous = React.useRef({ value: from, text: format(from) });

	useIsomorphicLayoutEffect(() => {
		const text = format(value);
		// Bailing out on an unchanged string keeps an inline `format` from looping.
		if (text === previous.current.text) {
			return;
		}

		const direction = value < previous.current.value ? -1 : 1;
		previous.current = { value, text };
		setFigure({ text, direction });
	}, [format, value]);

	return (
		<RollingFigure
			text={figure.text}
			direction={figure.direction}
			duration={prefersReducedMotion ? 0 : duration}
			ease={ease}
		/>
	);
}

/**
 * Interpolates the figure and reformats it every frame, so it ticks through the values
 * in between. The interpolation lives in a motion value, so counting updates the DOM
 * directly and never re-renders React.
 */
function Count({
	value,
	from,
	format,
	duration,
	ease,
	prefersReducedMotion,
}: {
	value: number;
	from: number;
	format: (value: number) => string;
	duration: number;
	ease: Easing | Easing[];
	prefersReducedMotion: boolean;
}) {
	const count = useMotionValue(from);
	const display = useTransform(() => format(count.get()));

	// Before paint, so a reduced-motion reader never sees `from` on screen.
	useIsomorphicLayoutEffect(() => {
		if (prefersReducedMotion) {
			count.jump(value);
			return;
		}

		const controls = animate(count, value, { duration, ease });
		return () => controls.stop();
	}, [count, duration, ease, prefersReducedMotion, value]);

	return <m.span>{display}</m.span>;
}

type AnimatedNumberOwnProps = {
	/** The figure to animate towards. Changing it retargets an in-flight animation. */
	value: number;
	/**
	 * Maps the figure to the string on screen. Receives the raw (unrounded) value, so a
	 * formatter is free to render decimals, or to break the number into compound units
	 * such as `"186h 35m"`.
	 */
	format?: (value: number) => string;
	/**
	 * `"roll"` slides each changed character vertically, like a flip clock. `"count"`
	 * interpolates instead, ticking through every value on the way.
	 */
	animation?: AnimatedNumberAnimation;
	/** The figure to animate from on mount. Pass `value` to skip the mount animation. */
	from?: number;
	/** Length of the animation in seconds. Defaults to `0.4` for a roll, `1.2` for a count. */
	duration?: number;
	/** Easing curve for the animation. */
	ease?: Easing | Easing[];
};

/**
 * Props for {@link AnimatedNumber}.
 *
 * Extends {@link TextProps} so `as`, `variant` and `className` pass straight through to
 * {@link Text}. `children` is omitted because the content is derived from `value` and `format`.
 *
 * @template T - The HTML element tag to render, defaults to `"p"`.
 */
export type AnimatedNumberProps<T extends TextElement = "p"> =
	AnimatedNumberOwnProps &
		Omit<TextProps<T>, keyof AnimatedNumberOwnProps | "children">;

function AnimatedNumberInner({
	value,
	format = formatInteger,
	animation = "roll",
	from = 0,
	duration,
	ease = "easeOut",
	className,
	...textProps
}: AnimatedNumberProps<TextElement>) {
	// `useReducedMotion` resolves on the client's first render but is false on the server,
	// so it gates the animation's length rather than what gets rendered.
	const prefersReducedMotion = useReducedMotion() ?? false;
	const figure = {
		value,
		from,
		format,
		duration: duration ?? defaultDurations[animation],
		ease,
		prefersReducedMotion,
	};

	return (
		// Tabular figures keep every digit the same width, so a rolling column does not
		// shove its neighbours sideways mid-animation.
		<Text
			className={cn("tabular-nums", className)}
			data-testid="animated-number"
			{...textProps}
		>
			<span aria-hidden="true" data-testid="animated-number-figure">
				<MotionFeatures>
					{animation === "roll" ? <Roll {...figure} /> : <Count {...figure} />}
				</MotionFeatures>
			</span>
			<span className="sr-only" data-testid="animated-number-value">
				{format(value)}
			</span>
		</Text>
	);
}

/**
 * Animates a figure into place instead of letting it snap, rendering through {@link Text}
 * so typography stays with the design system's variants.
 *
 * Two animations are available. `"roll"`, the default, slides each changed character
 * vertically the way a flip clock does. `"count"` interpolates the value instead, ticking
 * through the numbers in between — better when the point is accumulation rather than
 * change. Both leave presentation to `format`, which is what lets one component serve
 * counts, currency, percentages and compound durations alike.
 *
 * Accessibility is handled here so consumers do not have to: the animation is hidden from
 * assistive technology and exposed only as its final figure, and `prefers-reduced-motion`
 * skips the animation entirely.
 *
 * @param props.value - The figure to animate towards.
 * @param props.format - Maps the figure to a string. Defaults to a grouped whole number.
 * @param props.animation - `"roll"` (default) or `"count"`.
 * @param props.from - The figure to animate from on mount. Defaults to `0`.
 * @param props.duration - Length in seconds. Defaults to `0.4` for a roll, `1.2` for a count.
 * @param props.ease - Easing curve. Defaults to `"easeOut"`.
 */
const AnimatedNumber = AnimatedNumberInner as <T extends TextElement = "p">(
	props: AnimatedNumberProps<T>,
) => React.ReactElement | null;

export { AnimatedNumber };
